import axios from "axios";
import { Stock } from "@/models/stock";
import { currentLogosConfig } from "@/lib/logos";
import { stockLookupExchanges } from "@/lib/stock-url";
import { isEmpty } from "lodash";

/**
 * A stock lookup that has not answered in 3s is treated as an outage (a 503 on
 * the server path) rather than left to hold a request open.
 */
const STOCK_LOOKUP_TIMEOUT_MS = 3000;

export const getStockInfoOnClient = async (stockIdOrExchangeId: string, ticker?: string) => {
    const res = await axios.get<Stock>(`/API/v2/${ticker ? "s" : "id"}/${stockIdOrExchangeId}/${ticker ? `${ticker}/` : ""}`, { timeout: STOCK_LOOKUP_TIMEOUT_MS });

    if (res.data.currency === "GBP") {
        res.data.high_52_week = res.data.high_52_week * 100;
        res.data.low_52_week = res.data.low_52_week * 100;
        res.data.last_close = res.data.last_close * 100;
        res.data.previous_close = res.data.previous_close * 100;
    }
    return res.data;
};

/**
 * The stock API could not answer (5xx, timeout, network, or a body that is not
 * JSON). Distinct from "no such stock" so a page can answer 503 instead of 404:
 * a 404 during a backend outage tells Google to drop every stock page it crawls.
 */
export class StockBackendError extends Error {
    constructor(
        public status: number | null,
        message: string
    ) {
        super(message);
        this.name = "StockBackendError";
    }
}

/** One `/API/v2/...` stock lookup. Resolves `{}` for "no such stock" (400/404), throws StockBackendError otherwise. */
const fetchStockOnServer = async (path: string): Promise<Stock> => {
    // One deadline for the whole exchange, body included: a backend that sends headers and then
    // stalls the body must not hold the request open either.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), STOCK_LOOKUP_TIMEOUT_MS);
    try {
        let res: Response;
        try {
            res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/API/v2/${path}`, { signal: controller.signal });
        } catch (err) {
            throw new StockBackendError(null, `stock lookup failed: ${err}`);
        }
        // The API answers an unknown stock with 400 "No data for stock".
        if (res.status === 400 || res.status === 404) return {} as Stock;
        if (!res.ok) throw new StockBackendError(res.status, `stock lookup answered ${res.status}`);
        try {
            return (await res.json()) ?? ({} as Stock);
        } catch (err) {
            throw new StockBackendError(res.status, `stock lookup returned a non-JSON body or timed out: ${err}`);
        }
    } finally {
        clearTimeout(timer);
    }
};

/**
 * Server-side stock lookup by stock id, or by exchange + ticker.
 *
 * Resolves `{}` (check with `isEmpty`) when the stock does not exist and throws
 * StockBackendError when the API could not answer. Exchange lookups accept any
 * alias in `stockLookupExchanges` (`NasdaqGS`, `NASDAQ`, `nyse`, ...), because
 * the API does not resolve every exchange symbol it returns.
 */
export const getStockInfoOnServer = async (stockIdOrExchangeId: string, ticker?: string, trend?: boolean): Promise<Stock> => {
    const query = trend ? "?trend" : "";
    let stock = {} as Stock;
    if (ticker) {
        for (const exchange of stockLookupExchanges(stockIdOrExchangeId)) {
            stock = await fetchStockOnServer(`s/${encodeURIComponent(exchange)}/${encodeURIComponent(ticker)}/${query}`);
            if (!isEmpty(stock)) break;
        }
    } else {
        stock = await fetchStockOnServer(`id/${stockIdOrExchangeId}/${query}`);
    }
    if (stock.currency === "GBP") {
        stock.high_52_week = stock.high_52_week * 100;
        stock.low_52_week = stock.low_52_week * 100;
        stock.last_close = stock.last_close * 100;
        stock.previous_close = stock.previous_close * 100;
    }
    return stock;
};

export const getStockInfoByTicker = async (ticker: string): Promise<Stock> => {
    const res = await axios.get(`/API/v2/s/US/${ticker}/`);

    if (res.data.currency === "GBP") {
        res.data.high_52_week = res.data.high_52_week * 100;
        res.data.low_52_week = res.data.low_52_week * 100;
        res.data.last_close = res.data.last_close * 100;
        res.data.previous_close = res.data.previous_close * 100;
    }
    return res.data;
};

/**
 * Look up a Stock by Security id — used where the caller already has
 * the security_id from another payload (e.g. a universal flow row
 * carries it) so we skip the /find/ search. Hits the same endpoint
 * as the ticker / id lookups and returns the same Stock shape.
 */
export const getStockInfoBySecurityId = async (securityId: number): Promise<Stock> => {
    const res = await axios.get<Stock>(`/API/v2/sid/${securityId}/`);
    if (res.data.currency === "GBP") {
        res.data.high_52_week = res.data.high_52_week * 100;
        res.data.low_52_week = res.data.low_52_week * 100;
        res.data.last_close = res.data.last_close * 100;
        res.data.previous_close = res.data.previous_close * 100;
    }
    return res.data;
};

export const getNoBackgroundLogos = async (): Promise<number[]> => {
    const res = await axios.get<{ securityIds: number[] }>(`/interface/api/security/logos/no_background`);

    return res.data.securityIds;
};

export const getMissingLogo = async (security_id: number) => {
    if (!security_id) return;
    // Only the legacy origin is filled by asking web (which runs the FactSet importer into the
    // logo.ortex.com bucket). On the edge the logo service records the miss itself and its
    // refresh sweep sources the logo; asking web here only churns the legacy bucket.
    if (currentLogosConfig().source === "edge") return;

    const res = await axios.get(`/interface/api/security/${security_id}/logo`);

    return res.data;
};

export const getStockCommentary = async (exchange: string, stock: string) => {
    const res = await axios.get(`/API/v2/s/${exchange}/${stock}/stock_commentary`);

    return res.data;
};
